import { View, Text } from "@react-pdf/renderer";
import { stripHtmlToPlainText } from "../../../../../helpers/interventionHtml";
import { DPTOptions } from "../../../../patient/text/data";
import {
  DocumentationForms,
  isAnnualMedicationReviewIntervention,
} from "../../../../patient/Patient-Menu/Interventions/data";
import {
  AmrDisplayKind,
  formatAmrForDisplay,
  formatAmrAdherenceStatusForDocumentation,
} from "../../../../patient/clinical-services/service-steps/medication/data";
import { reasonOptions } from "../../../../patient/clinical-services/service-steps/clinical-assessment/data";
import { COLORS, styles } from "../../../../forms/pdf-components/PdfStyles";
import { PdfH2, PdfSubHeading, PdfBody } from "../../../../forms/pdf-components/PdfText";
import {
  PdfRow,
  PdfCol,
  PdfSection,
  PdfDivider,
  PdfVerticalDivider,
} from "../../../../forms/pdf-components/PdfLayout";
import { PdfDataField } from "../../../../forms/pdf-components/PdfDataField";

const getFirstNonEmpty = (...values) => {
  for (const v of values) {
    const s = String(v ?? "").trim();
    if (s.length > 0) return s;
  }
  return "";
};

const getReasonTakingLabel = (val) => {
  if (val == null || val === "") return "";
  const opt = reasonOptions.find((r) => r.value === val);
  return opt?.label || String(val);
};

/**
 * PdfDetailsSection
 *
 * Props:
 *   interventionDetails — { drugTherapyProblem, actionPlan, drugTherapySuggestions,
 *                           comments, pharmacistComments, medicationName, route, dosage,
 *                           form, frequency, whoChangedMedication, whyChangedMedication,
 *                           whenChangedMedication, reason, urgencyLevel }
 *   interventionType    — raw type string
 *   clinicalServiceName — raw service name string
 *   clinicalServiceTypeCode
 *   intervention        — full intervention object
 *   amrMedicationRow    — pre-computed from getAmrMedicationRowForIntervention (or null)
 */
export const PdfDetailsSection = ({
  interventionDetails = {},
  interventionType = null,
  clinicalServiceName = "",
  clinicalServiceTypeCode = "",
  intervention = null,
  amrMedicationRow = null,
}) => {
  const {
    drugTherapyProblem,
    actionPlan = [],
    drugTherapySuggestions,
    comments,
    pharmacistComments,
    medicationName,
    route,
    dosage,
    form: medForm,
    frequency,
    whoChangedMedication,
    whyChangedMedication,
    whenChangedMedication,
    reason: interventionReason,
    urgencyLevel,
  } = interventionDetails || {};

  // ── Adherence ──────────────────────────────────────────────────────────────
  const amrAdherence = getFirstNonEmpty(
    amrMedicationRow?.adherence,
    amrMedicationRow?.Adherence,
    amrMedicationRow?.consumption,
    amrMedicationRow?.Consumption,
    amrMedicationRow?.adherenceStatus,
    amrMedicationRow?.AdherenceStatus,
    interventionDetails?.adherence,
    interventionDetails?.Adherence,
    interventionDetails?.consumption,
    interventionDetails?.Consumption,
    intervention?.adherence,
    intervention?.Adherence,
    intervention?.consumption,
    intervention?.Consumption
  );

  const showAmrBlock =
    intervention != null && isAnnualMedicationReviewIntervention(intervention);

  const adherenceStatusDisplay =
    formatAmrAdherenceStatusForDocumentation(amrAdherence);

  // ── Reason taking ──────────────────────────────────────────────────────────
  const reasonTakingRaw = getFirstNonEmpty(
    interventionReason,
    interventionDetails?.Reason,
    amrMedicationRow?.reason,
    amrMedicationRow?.Reason
  );
  const reasonTakingDisplay = getReasonTakingLabel(reasonTakingRaw);

  // ── Pharmacist comments ────────────────────────────────────────────────────
  const rawComments = getFirstNonEmpty(
    comments,
    pharmacistComments,
    interventionDetails?.Comments,
    interventionDetails?.PharmacistComments
  );
  const pharmacistCommentsDisplay =
    stripHtmlToPlainText(rawComments).trim() || "No additional comments added";

  // ── Assessment comments ────────────────────────────────────────────────────
  const rawAssessment = getFirstNonEmpty(
    drugTherapySuggestions,
    interventionDetails?.DrugTherapySuggestions
  );
  const assessmentCommentsDisplay =
    stripHtmlToPlainText(rawAssessment).trim() || "No additional comments added";

  // ── Medication strings ─────────────────────────────────────────────────────
  const originalPrescription = medicationName
    ? `${medicationName} ${dosage || ""}`.trim()
    : "";
  const currentRegimen = `${medicationName || ""} ${route || ""} ${dosage || ""} ${
    medForm || ""
  } ${frequency || ""}`.trim();

  // ── Action plan ────────────────────────────────────────────────────────────
  const actionPlanDisplay =
    Array.isArray(actionPlan) && actionPlan.length > 0
      ? actionPlan[0] || actionPlan.join(", ")
      : "";

  // ── DTP label ──────────────────────────────────────────────────────────────
  const dtpOption = DPTOptions.find((o) => o.value === drugTherapyProblem);
  const drugTherapyProblemLabel = dtpOption
    ? dtpOption.label
    : drugTherapyProblem || "";

  // ── Urgency ────────────────────────────────────────────────────────────────
  const urgencyDisplay = urgencyLevel
    ? String(urgencyLevel).charAt(0).toUpperCase() +
      String(urgencyLevel).slice(1)
    : "";

  // ── Intervention type label ────────────────────────────────────────────────
  const interventionTypeLabel = (() => {
    if (!interventionType) return "";
    const match = DocumentationForms.find((f) => f.type === interventionType);
    return match ? match.name : interventionType;
  })();

  // ── Show/hide therapy change fields (non-AMR only) ─────────────────────────
  const hideTherapyChangeFields = !isAnnualMedicationReviewIntervention({
    clinicalServiceType: clinicalServiceName,
    clinicalServiceTypeCode,
  });

  return (
    <PdfSection>
      {/* ── Top grid: 2 columns ── */}
      <PdfRow style={{ marginBottom: 3 }}>
        <PdfCol>
          <PdfDataField label="Intervention Type:" value={interventionTypeLabel} />
        </PdfCol>
        <PdfCol>
          <PdfDataField label="Urgency Level:" value={urgencyDisplay} />
        </PdfCol>
      </PdfRow>

      <PdfRow style={{ marginBottom: 3 }}>
        <PdfCol>
          <PdfDataField
            label="Drug Therapy Problem (DTP):"
            value={drugTherapyProblemLabel}
            vertical
          />
        </PdfCol>
        <PdfCol>
          <PdfDataField label="Action Plan:" value={actionPlanDisplay} />
        </PdfCol>
      </PdfRow>

      {/* Divider */}
      <PdfDivider style={{ marginVertical: 3 }} />

      {/* Pharmacist comments (spans full width) */}
      <PdfDataField
        label="Pharmacist Intervention Comments:"
        value={pharmacistCommentsDisplay}
        vertical
      />

      {/* ── Divider before medication block ── */}
      <View
        style={{
          borderTopWidth: 1,
          borderTopColor: COLORS.border,
          marginTop: 4,
          marginHorizontal: -6,
        }}
      />

      {/* ── Two-column medication block ── */}
      <PdfRow style={{ marginTop: 4 }}>
        {/* Left col */}
        <PdfCol style={{ paddingRight: 4 }}>
          <PdfDataField
            label="Original Prescription:"
            value={originalPrescription}
            vertical
          />
          <View
            style={{
              borderTopWidth: 1,
              borderTopColor: COLORS.border,
              marginVertical: 3,
            }}
          />
          {showAmrBlock && (
            <View style={{ marginBottom: 3 }}>
              <PdfSubHeading style={{ marginBottom: 2 }}>
                Assessment Results
              </PdfSubHeading>
              <PdfDataField
                label="Medication Effectiveness:"
                value={formatAmrForDisplay(
                  AmrDisplayKind.CommonQuestion,
                  amrMedicationRow?.effectiveness ??
                    amrMedicationRow?.Effectiveness
                )}
              />
              <PdfDataField
                label="Safety / Side Effects:"
                value={formatAmrForDisplay(
                  AmrDisplayKind.CommonQuestion,
                  amrMedicationRow?.safety ?? amrMedicationRow?.Safety
                )}
              />
              <PdfDataField
                label="Appropriate Technique to Use:"
                value={formatAmrForDisplay(
                  AmrDisplayKind.CommonQuestion,
                  amrMedicationRow?.properTechnique ??
                    amrMedicationRow?.ProperTechnique,
                  { forTechnique: true }
                )}
              />
            </View>
          )}
          <PdfDataField label="Reason Taking:" value={reasonTakingDisplay} />
        </PdfCol>

        <PdfVerticalDivider style={{ marginVertical: 0 }} />

        {/* Right col */}
        <PdfCol style={{ paddingLeft: 4 }}>
          <PdfDataField
            label="Currently Taking:"
            value={currentRegimen}
            vertical
          />
          <View
            style={{
              borderTopWidth: 1,
              borderTopColor: COLORS.border,
              marginVertical: 3,
            }}
          />
          {!hideTherapyChangeFields && (
            <View style={{ marginBottom: 3 }}>
              <PdfSubHeading style={{ marginBottom: 2 }}>
                Medication Change Summary
              </PdfSubHeading>
              <PdfDataField
                label="Who changed the therapy?"
                value={whoChangedMedication || ""}
              />
              <PdfDataField
                label="Why was the therapy changed?"
                value={whyChangedMedication || ""}
              />
              <PdfDataField
                label="When was the therapy changed?"
                value={whenChangedMedication || ""}
              />
            </View>
          )}
          {showAmrBlock && (
            <View style={{ flexDirection: "row", flexWrap: "wrap", marginTop: 6 }}>
              <Text style={[styles.inlineBold, { marginRight: 2 }]}>
                Adherence Status:{" "}
              </Text>
              <Text style={styles.inlineRegular}>{adherenceStatusDisplay}</Text>
            </View>
          )}
        </PdfCol>
      </PdfRow>

      {/* Assessment comments box */}
      <View
        style={{
          borderWidth: 1,
          borderColor: COLORS.border,
          backgroundColor: COLORS.bg,
          padding: 5,
          marginTop: 4,
        }}
      >
        <Text style={styles.inlineBold}>Pharmacist Assessment Comments: </Text>
        <Text style={styles.inlineRegular}>{assessmentCommentsDisplay}</Text>
      </View>
    </PdfSection>
  );
};
